import React, { useCallback, useEffect, useRef, useState } from "react";
import { createGame, changeDirection, tick } from "./engine.js";
import type { Direction, SnakeGame } from "./types.js";

const CELL = 20;
const COLS = 25;
const ROWS = 25;

const COLORS = {
  bg: "#010a18",
  grid: "rgba(0, 180, 255, 0.06)",
  snake: "#00b4ff",
  snakeGlow: "rgba(0, 180, 255, 0.5)",
  head: "#00ffea",
  headGlow: "rgba(0, 255, 234, 0.6)",
  food: "#ff4d8d",
  foodGlow: "rgba(255, 77, 141, 0.5)",
  text: "#8ab4d0",
};

function draw(ctx: CanvasRenderingContext2D, game: SnakeGame, cellSize: number) {
  const w = game.gridCols * cellSize;
  const h = game.gridRows * cellSize;

  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, w, h);

  for (let x = 0; x < game.gridCols; x++) {
    for (let y = 0; y < game.gridRows; y++) {
      ctx.fillStyle = (x + y) % 2 === 0 ? COLORS.grid : "transparent";
      ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
    }
  }

  ctx.shadowColor = COLORS.foodGlow;
  ctx.shadowBlur = 10;
  ctx.fillStyle = COLORS.food;
  const fx = game.food.x * cellSize + cellSize / 2;
  const fy = game.food.y * cellSize + cellSize / 2;
  ctx.beginPath();
  ctx.arc(fx, fy, cellSize / 2 - 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowBlur = 0;

  game.snake.forEach((seg, i) => {
    const sx = seg.x * cellSize;
    const sy = seg.y * cellSize;
    const pad = 1;
    const r = 3;

    if (i === 0) {
      ctx.shadowColor = COLORS.headGlow;
      ctx.shadowBlur = 12;
      ctx.fillStyle = COLORS.head;
    } else {
      ctx.shadowColor = COLORS.snakeGlow;
      ctx.shadowBlur = 6;
      ctx.fillStyle = COLORS.snake;
    }

    ctx.beginPath();
    ctx.roundRect(sx + pad, sy + pad, cellSize - pad * 2, cellSize - pad * 2, r);
    ctx.fill();
  });
  ctx.shadowBlur = 0;
}

export default function SnakePage({ hideTitle = false }: { hideTitle?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef<SnakeGame>(createGame(COLS, ROWS));
  const animRef = useRef(0);
  const lastTickRef = useRef(0);
  const cellSizeRef = useRef(20);

  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [isOver, setIsOver] = useState(false);
  const [isPaused, setIsPaused] = useState(true);

  const syncState = useCallback((g: SnakeGame) => {
    gameRef.current = g;
    setScore(g.score);
    setHighScore(g.highScore);
    setIsOver(g.isOver);
    setIsPaused(g.isPaused);
  }, []);

  const reset = useCallback(() => {
    const g = createGame(COLS, ROWS);
    const withHigh = { ...g, highScore: gameRef.current.highScore };
    syncState(withHigh);
  }, [syncState]);

  const startGame = useCallback(() => {
    if (isOver) { reset(); return; }
    const g = { ...gameRef.current, isPaused: false };
    syncState(g);
  }, [isOver, reset, syncState]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const maxW = Math.min(canvas.clientWidth, COLS * CELL);
    const displaySize = Math.floor(maxW / COLS) * COLS || COLS * 10;
    const cellSize = displaySize / COLS;
    cellSizeRef.current = cellSize;

    canvas.width = COLS * cellSize * dpr;
    canvas.height = ROWS * cellSize * dpr;
    canvas.style.width = `${COLS * cellSize}px`;
    canvas.style.height = `${ROWS * cellSize}px`;
    ctx.scale(dpr, dpr);

    function frame(now: number) {
      const g = gameRef.current;
      if (!g.isOver && !g.isPaused) {
        if (now - lastTickRef.current >= g.speed) {
          const next = tick(g);
          syncState(next);
          lastTickRef.current = now;
        }
      }
      draw(ctx!, g, cellSize);
      animRef.current = requestAnimationFrame(frame);
    }

    animRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(animRef.current);
  }, [syncState]);

  // Mouse direction
  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const g = gameRef.current;
    if (g.isOver || g.isPaused) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cellSize = cellSizeRef.current;
    const gridX = Math.floor((e.clientX - rect.left) / (rect.width / g.gridCols));
    const gridY = Math.floor((e.clientY - rect.top) / (rect.height / g.gridRows));

    if (gridX < 0 || gridX >= g.gridCols || gridY < 0 || gridY >= g.gridRows) return;

    const head = g.snake[0];
    const dx = gridX - head.x;
    const dy = gridY - head.y;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    let dir: Direction | null = null;
    if (absDx > absDy && dx !== 0) dir = dx > 0 ? "RIGHT" : "LEFT";
    else if (dy !== 0) dir = dy > 0 ? "DOWN" : "UP";
    if (!dir) return;

    const updated = changeDirection(g, dir);
    if (updated !== g) syncState(updated);
  }

  function handleCanvasClick() {
    const g = gameRef.current;
    if (g.isOver) reset();
    else if (g.isPaused) startGame();
  }

  // Keyboard for Space/Enter to start/pause
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        if (gameRef.current.isOver) reset();
        else if (gameRef.current.isPaused) startGame();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [reset, startGame]);

  const status = isOver
    ? `Game Over — Score: ${score}`
    : isPaused
      ? "Move mouse over the grid and click to start"
      : `Score: ${score}`;

  return (
    <div className="game-shell">
      {!hideTitle && <h1 className="title">Snake</h1>}

      <section className="control-panel">
        <div className="row two-col">
          <label>
            Mode
            <select value="single">
              <option value="single">Single Player</option>
            </select>
          </label>
          <label>
            Grid
            <select
              value={`${COLS}x${ROWS}`}
              onChange={(e) => {
                const [c, r] = e.target.value.split("x").map(Number);
                const g = createGame(c, r);
                syncState({ ...g, highScore: gameRef.current.highScore });
                lastTickRef.current = 0;
              }}
            >
              <option value="40x40">Large (40×40)</option>
              <option value="20x20">Standard (20×20)</option>
              <option value="15x15">Small (15×15)</option>
            </select>
          </label>
        </div>

        <div className="snake-score-row">
          <div>Score <span>{score}</span></div>
          <div>Best <span>{highScore}</span></div>
        </div>

        <div className="actions" style={{ justifyContent: "center" }}>
          {!isOver && isPaused && <button onClick={startGame}>Start Game</button>}
          {isOver && <button onClick={reset}>Play Again</button>}
        </div>

        <p className="subtle" style={{ textAlign: "center" }}>{status}</p>
      </section>

      <section className="board-panel" style={{ position: "relative" }}>
        <p className="status" style={{ textAlign: "center", marginBottom: 8 }}>
          {isOver ? `Final Score: ${score}` : isPaused ? "Paused" : `Score: ${score}`}
        </p>
        <div className="snake-canvas-wrap">
          <canvas
            ref={canvasRef}
            className="snake-canvas"
            onMouseMove={handleMouseMove}
            onClick={handleCanvasClick}
          />
        </div>
      </section>
    </div>
  );
}
